import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserDetails } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { formatMemberExpiry } from '../../utils/membership.util';

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-profile-modal.component.html',
  styleUrl: './user-profile-modal.component.scss'
})
export class UserProfileModalComponent implements OnInit {
  @Input() userDetails: UserDetails | null = null;
  @Output() close = new EventEmitter<void>();

  activeTab: 'profile' | 'password' = 'profile';
  passwordForm: FormGroup;
  isSubmitting = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    console.log('User Profile Modal opened with data:', this.userDetails);
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    return null;
  }

  switchTab(tab: 'profile' | 'password'): void {
    this.activeTab = tab;
    this.clearMessages();
  }

  onClose(): void {
    this.close.emit();
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid) {
      Object.keys(this.passwordForm.controls).forEach(key => {
        this.passwordForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;
    this.clearMessages();

    const { currentPassword, newPassword } = this.passwordForm.value;

    // TODO: Implement actual password change API call
    // For now, simulate API call
    setTimeout(() => {
      this.isSubmitting = false;
      this.successMessage = 'เปลี่ยนรหัสผ่านสำเร็จ!';
      this.passwordForm.reset();
      
      // Auto close success message after 3 seconds
      setTimeout(() => {
        this.successMessage = null;
      }, 3000);
    }, 1000);
  }

  clearMessages(): void {
    this.successMessage = null;
    this.errorMessage = null;
  }

  // Getters for template
  get userName(): string {
    if (this.userDetails?.userInformation) {
      const firstNameThai = this.userDetails.userInformation.firstNameThai || '';
      const lastNameThai = this.userDetails.userInformation.lastNameThai || '';
      const firstName = this.userDetails.userInformation.firstName || '';
      const lastName = this.userDetails.userInformation.lastName || '';
      
      const name = firstNameThai || firstName;
      const surname = lastNameThai || lastName;
      
      return `${name} ${surname}`.trim() || '-';
    }
    return '-';
  }

  get userEmail(): string {
    return this.userDetails?.email || '-';
  }

  get userCid(): string {
    return this.userDetails?.cid || '-';
  }

  get userPhone(): string {
    return this.userDetails?.userInformation?.phoneNumber || '-';
  }

  get userOrganization(): string {
    return this.userDetails?.userInformation?.workplaceName || 
           this.userDetails?.userInformation?.organization || '-';
  }

  get userRole(): string {
    return this.userDetails?.userRole?.role?.name || '-';
  }

  get userRank(): string {
    return this.userDetails?.userRank?.rank?.name || '-';
  }

  get vatId(): string {
    return this.userDetails?.userInformation?.vatId || '-';
  }

  /**
   * วันหมดอายุบัตรสมาชิก (ตลอดชีพจะแสดงคำว่า "ตลอดชีพ")
   */
  get vatExpiryText(): string {
    const info = this.userDetails?.userInformation;
    return formatMemberExpiry(info?.vatType, info?.vatExpired, date => this.formatDate(date));
  }

  get valuerId(): string {
    return this.userDetails?.userInformation?.valuerId || '-';
  }

  get currentAddress(): string {
    const info = this.userDetails?.userInformation;
    if (!info) return '-';
    
    const parts = [
      info.currentAddress || info.address,
      info.currentSubdistrict || info.subdistrict,
      info.currentDistrict || info.district,
      info.currentProvince || info.province,
      info.currentPostalCode || info.postalCode
    ].filter(Boolean);
    
    return parts.length > 0 ? parts.join(' ') : '-';
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }
}
